import {
  Dialog,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import React, { Fragment, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Close } from '@mui/icons-material'
import LoadingIndicator from '@/components/LoadingIndicator'
import CCMenuItemCard from '../ItemCard/MenuItemCard'
import MenuItemDetail from '../ItemDetail/MenuItemDetail'
import { CartMappingOptionInput, graphqlDataToCartItem } from './cartMapping'
import { CanteenCartItemInput } from '../MenuCart/types'

const canteenMenuItemsUserDataFragment = graphql(`
  fragment CanteenMenuItems_UserDataFragment on User {
    ...CanteenMenuItemCard_UserDataFragment
    ...CanteenMenuItemDetail_UserDataFragment
  }
`)

const canteenMenuItemsMenuItemDataQuery = graphql(`
  query CanteenMenuItemModal_ItemDataQuery($vendorId: ID!, $itemId: ID!) {
    vendor(id: $vendorId) {
      ...CanteenMenuItemDetail_VendorDataFragment
      liveMenuItem(id: $itemId) {
        _id
        ...CanteenMenuItemDetail_ItemDataFragment
      }
    }
  }
`)

const canteenMenuItemsMenuItemDataFragment = graphql(`
  fragment CanteenMenuItems_MenuItemDataFragment on CCMenuItem {
    id
    ...CanteenMenuItemCard_MenuItemFragment
    ...CartMapping_ItemDataFragment
    liveVariation {
      _id
    }
    liveMenuItem {
      _id
      deleted
      deletedAt
      isActive
      isMenuItem
    }
  }
`)

const canteenMenuItemsVendorDataFragment = graphql(`
  fragment CanteenMenuItems_VendorDataFragment on Vendor {
    id
    ...CanteenMenuItemCard_VendorDataFragment
  }
`)

type Props = {
  vendorMenus: Array<{
    vendor: FragmentType<typeof canteenMenuItemsVendorDataFragment>
    mains: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
    sides: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
    drinks: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
  }>
  userData: FragmentType<typeof canteenMenuItemsUserDataFragment>
  addToCart: (item: CanteenCartItemInput) => void
  cartItems: string[]
  loading: boolean
}

const MenuItems: React.FC<Props> = ({
  userData: userDataProp,
  addToCart,
  cartItems,
  vendorMenus = [],
  loading,
}) => {
  const userData = useFragment(canteenMenuItemsUserDataFragment, userDataProp)

  const theme = useTheme()
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'))
  const fullScreenItemModal = useMediaQuery(theme.breakpoints.down('md'))

  const [selectedItem, setSelectedItem] = useState<{
    vendorId: string
    itemId: string
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>
    ccVariationId: string
  } | null>(null)
  const [getItemData, { data: selectedItemData, loading: loadingItem }] = useLazyQuery(
    canteenMenuItemsMenuItemDataQuery,
  )

  const mains = vendorMenus.flatMap((menu) =>
    menu.mains.map((item) => ({ item, vendor: menu.vendor })),
  )
  const sides = vendorMenus.flatMap((menu) =>
    menu.sides.map((item) => ({ item, vendor: menu.vendor })),
  )
  const drinks = vendorMenus.flatMap((menu) =>
    menu.drinks.map((item) => ({ item, vendor: menu.vendor })),
  )

  const skeletonRenderArray = [1, 2, 3, 4, 5, 6]
  if (isLargeDesktop) skeletonRenderArray.push(7, 8, 9)

  const handleSeeMore = (
    itemId: string,
    vendorId: string,
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
  ) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { liveVariation } = useFragment(canteenMenuItemsMenuItemDataFragment, itemData)
    getItemData({ variables: { vendorId, itemId } })
    setSelectedItem({
      vendorId,
      itemId,
      itemData,
      ccVariationId: liveVariation._id,
    })
  }

  const handleAddToCart = (
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
    ccVariationId: string,
    quantity: number,
    options: Array<CartMappingOptionInput>,
  ) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const item = useFragment(canteenMenuItemsMenuItemDataFragment, itemData)
    const mappedCartItem = graphqlDataToCartItem(item, { quantity, ccVariationId }, options)
    addToCart(mappedCartItem)
    setSelectedItem(null)
  }

  return (
    <>
      <Dialog
        sx={{
          '.MuiPaper-root': {
            height: { md: '93vh', xs: '100%' },
            minHeight: { md: '93vh', xs: '100%' },
            overflow: 'scroll',
          },
        }}
        fullScreen={fullScreenItemModal}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
        open={Boolean(selectedItem?.itemId)}
      >
        <Stack direction="row" position="sticky" top={0} right={0} zIndex={5}>
          <IconButton
            onClick={() => setSelectedItem(null)}
            sx={{ zIndex: 5, position: 'absolute', right: 0 }}
          >
            <Close />
          </IconButton>
        </Stack>
        {loadingItem ? <LoadingIndicator /> : null}
        {selectedItemData && selectedItem && !loadingItem ? (
          <MenuItemDetail
            onAddToCart={(quantity, options) => {
              handleAddToCart(selectedItem.itemData, selectedItem.ccVariationId, quantity, options)
            }}
            selectedVariationId={selectedItem.ccVariationId}
            itemData={selectedItemData.vendor.liveMenuItem}
            vendorData={selectedItemData.vendor}
            userData={userData}
          />
        ) : null}
      </Dialog>
      <Grid spacing={1} width="100%" container alignItems="stretch">
        {loading ? (
          <>
            <Grid item xs={12} marginTop={1}>
              <Skeleton variant="rectangular" height="40px" width="100%" />
            </Grid>
            {skeletonRenderArray.map((num) => (
              <Grid key={num} md={6} lg={6} xl={4} xs={12} item>
                <Skeleton height="200px" width="100%" variant="rectangular" />
              </Grid>
            ))}
          </>
        ) : null}
        {[
          { items: mains, label: 'Mains' },
          { items: sides, label: 'Sides' },
          { items: drinks, label: 'Drinks' },
        ].map((itemType) => {
          return itemType.items.length ? (
            <Fragment key={itemType.label}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{itemType.label}</Typography>
              </Grid>
              {itemType.items.map((item) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const itemData = useFragment(canteenMenuItemsMenuItemDataFragment, item.item)
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const vendorData = useFragment(canteenMenuItemsVendorDataFragment, item.vendor)
                return (
                  <Grid md={6} lg={6} xl={4} xs={12} key={itemData.liveMenuItem._id} item>
                    <CCMenuItemCard
                      showHighlighted={cartItems.includes(itemData.liveMenuItem._id)}
                      onSeeMoreClick={() =>
                        handleSeeMore(itemData.liveMenuItem._id, vendorData.id, item.item)
                      }
                      onAddToCart={() => {
                        handleAddToCart(item.item, itemData.liveVariation._id, 1, [])
                      }}
                      vendorData={vendorData}
                      userData={userData}
                      itemData={itemData}
                    />
                  </Grid>
                )
              })}
            </Fragment>
          ) : null
        })}
      </Grid>
    </>
  )
}

export default MenuItems
